import { type FC } from 'react';
import { useUnit } from 'effector-react';
import { Pagination, ScrollArea, Stack } from '@mantine/core';
import { type PayoutCascadeGroupFilters, type PayoutGroup, usePayoutCascadeGroupsList } from '~/entities/payout';
import { type PaginationModel } from '~/shared/lib/pagination';
import { Table } from '~/shared/ui/Table';
import { createTableData } from './tableUtils';

interface CascadeGroupsListProps {
    filter?: PayoutCascadeGroupFilters;
    paginationModel: PaginationModel;
    onClick?: (group: PayoutGroup) => void;
}

export const CascadeGroupsList: FC<CascadeGroupsListProps> = ({ filter, onClick, paginationModel }) => {
    const pagination = useUnit(paginationModel.$pagination);

    const groups = usePayoutCascadeGroupsList({
        'pagination.page': pagination.page.toString(),
        'pagination.perPage': pagination.perPage.toString(),
        ...(filter?.name?.value && { 'filter.name.value': filter.name.value }),
        ...(filter?.id?.value && { 'filter.id.value': filter.id.value }),
    });

    const total = Number(groups.data?.pagination?.total);
    const totalPages = Math.ceil(total / pagination.perPage);

    const tableData = createTableData({
        groups: groups.data?.items,
    });

    const handleRowClick = (item: PayoutGroup) => {
        return onClick?.(item);
    };

    const handleChangeCurrentPage = (page: number) => {
        paginationModel.setPage(page - 1);
    };

    return (
        <Stack>
            <ScrollArea h={512}>
                <Table
                    striped
                    highlightOnHover
                    data={tableData}
                    onRowClick={handleRowClick}
                    loading={groups.isLoading}
                />
            </ScrollArea>

            {totalPages > 1 && (
                <Pagination
                    total={totalPages}
                    value={pagination.page + 1}
                    onChange={handleChangeCurrentPage}
                    size='sm'
                />
            )}
        </Stack>
    );
};
